import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Settings, User, Lock, CreditCard, BarChart3 } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import GeneralSettings from '../components/settings/GeneralSettings';
import AccountSettings from '../components/settings/AccountSettings';
import PrivacySettings from '../components/settings/PrivacySettings';
import BillingSettings from '../components/settings/BillingSettings';
import UsageSettings from '../components/settings/UsageSettings';

const SettingsPage = () => {
  const location = useLocation();
  const { isCollapsed } = useSidebar();
  const [activeSection, setActiveSection] = useState('general');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    if (section) {
      setActiveSection(section);
    }
  }, [location]);

  const settingsSections = [
    {
      title: 'GENERAL',
      items: [
        { id: 'general', label: 'General', icon: Settings },
        { id: 'account', label: 'Account', icon: User },
        { id: 'privacy', label: 'Privacy', icon: Lock },
      ]
    },
    {
      title: 'BILLING',
      items: [
        { id: 'billing', label: 'Billing', icon: CreditCard },
        { id: 'usage', label: 'Usage', icon: BarChart3 },
      ]
    }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return <GeneralSettings />;
      case 'account':
        return <AccountSettings />;
      case 'privacy':
        return <PrivacySettings />;
      case 'billing':
        return <BillingSettings />;
      case 'usage':
        return <UsageSettings />;
      default:
        return <GeneralSettings />;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-bg-gray dark:bg-dark-bg">
      {/* Main App Sidebar */}
      <Sidebar />
      
      {/* Settings Content Area */}
      <div className={`flex flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        {/* Settings Sidebar - NO BORDER, NO BACKGROUND */}
        <aside className="w-64 h-screen overflow-y-auto border-r border-gray-200 dark:border-[#2e2e2d]">
          <div className="p-6">
            <h1 className="text-2xl font-bold text-text-dark dark:text-dark-text mb-6">Settings</h1>

            {settingsSections.map((section) => (
              <div key={section.title} className="mb-6">
                <h2 className="text-xs font-semibold text-text-gray dark:text-dark-text-secondary uppercase tracking-wider mb-2 px-3">
                  {section.title}
                </h2>
                
                <nav className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveSection(item.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                          isActive
                            ? 'bg-gray-100 dark:bg-dark-bg-tertiary text-text-dark dark:text-dark-text font-medium'
                            : 'text-text-gray dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover/50 hover:text-text-dark dark:hover:text-dark-text'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </button>
                    );
                  })}
                </nav>
              </div>
            ))}
          </div>
        </aside>

        {/* Settings Content */}
        <main className="flex-1 h-screen overflow-y-auto bg-muted/30">
          <div className="max-w-4xl mx-auto p-8">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
